import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import {
  fetchPacks,
  formatPrice,
  startCheckout,
  type CreditPack,
} from '@/lib/billing';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Story 50.7 — buy-credits modal. Lists the tenant-available packs and
 * starts a checkout session against the configured BillingProvider on
 * click. The gateway URL replaces window.location, so when the user
 * returns from the gateway success/cancel page the app reloads with
 * the freshest balance.
 */
export function BuyCreditsModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const [packs, setPacks] = useState<CreditPack[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submittingCode, setSubmittingCode] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadError(null);
    fetchPacks()
      .then((rows) => {
        if (!cancelled) setPacks(rows);
      })
      .catch(() => {
        if (!cancelled) setLoadError(t('billing.modal.load_error'));
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  if (!open) return null;

  async function handleBuy(pack: CreditPack) {
    setSubmittingCode(pack.code);
    setSubmitError(null);
    try {
      const successUrl = `${window.location.origin}/billing/success`;
      const cancelUrl = `${window.location.origin}/billing/cancel`;
      const session = await startCheckout(pack.code, successUrl, cancelUrl);
      window.location.href = session.redirectUrl;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('billing.modal.checkout_error');
      setSubmitError(msg);
      setSubmittingCode(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-2 text-lg font-semibold">{t('billing.modal.title')}</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          {t('billing.modal.subtitle')}
        </p>

        {loadError && (
          <div className="mb-3 rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
            {loadError}
          </div>
        )}

        {packs === null && !loadError && (
          <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        )}

        {packs && packs.length === 0 && (
          <div className="text-sm text-muted-foreground">{t('billing.modal.no_packs')}</div>
        )}

        {packs && packs.length > 0 && (
          <ul className="space-y-2">
            {packs.map((pack) => (
              <li
                key={pack.code}
                className="flex items-center justify-between rounded-md border bg-surface-1 px-4 py-3"
              >
                <div>
                  <div className="font-medium">{pack.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {pack.description ?? `${pack.credits} ${t('billing.credits_unit')}`}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-semibold">
                      {formatPrice(pack.priceCents, pack.currency)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {pack.credits.toLocaleString()} {t('billing.credits_unit')}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleBuy(pack)}
                    disabled={submittingCode !== null}
                  >
                    {submittingCode === pack.code
                      ? t('billing.modal.redirecting')
                      : t('billing.modal.buy')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {submitError && (
          <div className="mt-3 rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
            {submitError}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button variant="ghost" onClick={onClose} disabled={submittingCode !== null}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}
